import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { closeSnackbar, enqueueSnackbar } from 'notistack'
import { Button } from '@mui/material'
import API from '../API'
import type { User } from './auth'
import { BoardguardTicket, downloadTicketPdf, NoShowFields } from '../utils/boardguard'

export interface AppNotification {
    id: number
    created_at: string
    type: string
    title: string
    message: string
    order_id: number | null
    read_at: string | null
    order?: {
        id: number
        reservation_code: string | null
        boardguard_ticket?: Pick<BoardguardTicket, 'pdf_ready' | 'status'> | null
    } | null
}

export interface OrderBoardguardEvent extends NoShowFields {
    order_id: number
    boardguard_ticket: BoardguardTicket | null
}

type OrderBoardguardListener = (event: OrderBoardguardEvent) => void

interface NotificationsContextValue {
    enabled: boolean
    notifications: AppNotification[]
    unread: number
    markRead: (id: number) => Promise<void>
    markAllRead: () => Promise<void>
    subscribeOrderUpdates: (listener: OrderBoardguardListener) => () => void
}

const NotificationsContext = createContext<NotificationsContextValue>({
    enabled: false,
    notifications: [],
    unread: 0,
    markRead: async () => {},
    markAllRead: async () => {},
    subscribeOrderUpdates: () => () => {},
})

const STAFF_ROLES = ['ADMIN', 'SELLER', 'AGENCY']

class FatalStreamError extends Error {}

export const getNotificationOrderUrl = (orderId: number) => `/admin/emissoes/${orderId}`

export function NotificationsProvider({ children, me }: PropsWithChildren<{ me: User | null }>) {
    const [notifications, setNotifications] = useState<AppNotification[]>([])
    const [unread, setUnread] = useState(0)
    const listenersRef = useRef(new Set<OrderBoardguardListener>())
    const enabled = !!me && STAFF_ROLES.includes(me.role) && window.location.pathname.startsWith('/admin')

    const notificationsRef = useRef(notifications)
    useEffect(() => {
        notificationsRef.current = notifications
    }, [notifications])

    const markRead = useCallback(async (id: number) => {
        const target = notificationsRef.current.find((item) => item.id === id)
        if (target?.read_at) return
        if (target) setUnread((count) => Math.max(0, count - 1))
        setNotifications((prev) =>
            prev.map((item) => (item.id === id ? { ...item, read_at: item.read_at ?? new Date().toISOString() } : item))
        )
        try {
            await API.post(`/notification/${id}/read`)
        } catch {
            // best effort
        }
    }, [])

    const markAllRead = useCallback(async () => {
        setNotifications((prev) => prev.map((item) => ({ ...item, read_at: item.read_at ?? new Date().toISOString() })))
        setUnread(0)
        try {
            await API.post('/notification/read-all')
        } catch {
            // best effort
        }
    }, [])

    const subscribeOrderUpdates = useCallback((listener: OrderBoardguardListener) => {
        listenersRef.current.add(listener)
        return () => {
            listenersRef.current.delete(listener)
        }
    }, [])

    const handleNotification = useCallback((notification: AppNotification) => {
        setNotifications((prev) => [notification, ...prev.filter((item) => item.id !== notification.id)].slice(0, 30))
        setUnread((count) => count + 1)

        const orderId = notification.order_id
        const isReady = notification.type === 'boardguard.ticket_ready'

        enqueueSnackbar(`${notification.title}: ${notification.message}`, {
            variant: isReady ? 'success' : notification.type === 'boardguard.attention' ? 'warning' : 'error',
            autoHideDuration: orderId ? 10000 : 5000,
            action: orderId
                ? (key) => (
                    <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                            closeSnackbar(key)
                            void markRead(notification.id)
                            if (isReady) {
                                void downloadTicketPdf(orderId, notification.order?.reservation_code)
                            } else {
                                window.location.href = getNotificationOrderUrl(orderId)
                            }
                        }}
                    >
                        {isReady ? 'Baixar PDF (PT)' : 'Ver emissão'}
                    </Button>
                )
                : undefined,
        })
    }, [markRead])

    const handleNotificationRef = useRef(handleNotification)
    useEffect(() => {
        handleNotificationRef.current = handleNotification
    }, [handleNotification])

    useEffect(() => {
        if (!enabled) return

        let cancelled = false
        API.get<{ rows: AppNotification[], unread: number }>('/notification')
            .then((response) => {
                if (cancelled || !Array.isArray(response?.data?.rows)) return
                setNotifications(response.data.rows)
                setUnread(response.data.unread)
            })
            .catch(() => {})

        const controller = new AbortController()
        void fetchEventSource(`${process.env.REACT_APP_API_URL}/notification/stream`, {
            signal: controller.signal,
            openWhenHidden: true,
            headers: {
                Authorization: `Bearer ${window.localStorage.getItem('@next-pontos/token') ?? ''}`,
            },
            async onopen(response) {
                if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) return
                if (response.status === 401 || response.status === 403) throw new FatalStreamError()
                throw new Error(`stream HTTP ${response.status}`)
            },
            onmessage(message) {
                if (!message.data) return
                try {
                    const data = JSON.parse(message.data)
                    if (message.event === 'notification') {
                        handleNotificationRef.current(data as AppNotification)
                    } else if (message.event === 'order.boardguard') {
                        listenersRef.current.forEach((listener) => listener(data as OrderBoardguardEvent))
                    }
                } catch {
                    // ignore malformed events
                }
            },
            onerror(error) {
                if (error instanceof FatalStreamError) throw error
                return 5000
            },
        }).catch(() => {})

        return () => {
            cancelled = true
            controller.abort()
        }
    }, [enabled])

    const value = useMemo<NotificationsContextValue>(
        () => ({ enabled, notifications, unread, markRead, markAllRead, subscribeOrderUpdates }),
        [enabled, notifications, unread, markRead, markAllRead, subscribeOrderUpdates]
    )

    return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
}

export function useNotifications() {
    return useContext(NotificationsContext)
}

export function useOrderBoardguardUpdates(listener: OrderBoardguardListener) {
    const { subscribeOrderUpdates } = useNotifications()
    const listenerRef = useRef(listener)
    useEffect(() => {
        listenerRef.current = listener
    }, [listener])

    useEffect(() => subscribeOrderUpdates((event) => listenerRef.current(event)), [subscribeOrderUpdates])
}
